import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";

const CATEGORIES = [
  "Electronics", "Machinery", "Textiles & Clothing", "Food & Beverage",
  "Jewellery & Luxury", "Furniture & Home Decor", "Chemicals & Plastics",
  "Packaging", "Auto Parts", "Beauty & Fragrance", "Construction Materials",
  "Safety Equipment", "Smart Devices & IoT", "Other",
];

const COUNTRIES = [
  "UAE", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
  "USA", "UK", "Germany", "France", "China", "India", "Australia",
  "Canada", "Japan", "South Korea", "Singapore", "Other",
];

const TARGET_COUNTRIES = [
  "Dubai, UAE", "Riyadh, Saudi Arabia", "Doha, Qatar",
  "All Middle East", "Any Country",
];

interface RFQEntry {
  id: string;
  buyer: string;
  country: string;
  product: string;
  category: string;
  qty: string;
  budget: string;
  date: string;
  responses: number;
  status: "open" | "closed";
}

const recentRFQs: RFQEntry[] = [
  {
    id: "rfq1",
    buyer: "James M.",
    country: "UK",
    product: "Industrial LED Panels 200W",
    category: "Electronics",
    qty: "500 units",
    budget: "$20,000–$30,000",
    date: "2026-04-13",
    responses: 4,
    status: "open",
  },
  {
    id: "rfq2",
    buyer: "Yuki T.",
    country: "Japan",
    product: "Arabic Oud Perfume Gift Sets",
    category: "Beauty & Fragrance",
    qty: "1,000 units",
    budget: "$25,000–$40,000",
    date: "2026-04-12",
    responses: 7,
    status: "open",
  },
  {
    id: "rfq3",
    buyer: "Amara O.",
    country: "Ghana",
    product: "Premium Medjool Dates 1kg Boxes",
    category: "Food & Beverage",
    qty: "2,000 boxes",
    budget: "$40,000–$60,000",
    date: "2026-04-11",
    responses: 3,
    status: "open",
  },
  {
    id: "rfq4",
    buyer: "Carlos M.",
    country: "USA",
    product: "Smart Home Security Cameras 4K",
    category: "Electronics",
    qty: "200 units",
    budget: "$6,000–$10,000",
    date: "2026-04-10",
    responses: 9,
    status: "closed",
  },
  {
    id: "rfq5",
    buyer: "Priya S.",
    country: "India",
    product: "22K Gold Bangle Sets",
    category: "Jewellery & Luxury",
    qty: "50 sets",
    budget: "$30,000–$50,000",
    date: "2026-04-09",
    responses: 5,
    status: "open",
  },
  {
    id: "rfq6",
    buyer: "Lars E.",
    country: "Sweden",
    product: "Custom Corporate Uniform Shirts",
    category: "Textiles & Clothing",
    qty: "500 pieces",
    budget: "$3,000–$6,000",
    date: "2026-04-08",
    responses: 6,
    status: "closed",
  },
];

const RFQPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<"submit" | "browse">("submit");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      await fetch("https://readdy.ai/api/form/d7ekgvl37cef9t402hkg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // silent
    }
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Request for Quote (RFQ) – Get Factory Quotes | SuperMiddleEast"
        description="Submit your buying requirements and receive competitive quotes from verified Middle East factories. Compare quotes from Dubai, Saudi Arabia, Qatar, Turkey and Pakistan manufacturers."
        keywords="request for quote, RFQ Middle East, factory quote, bulk order quote, Dubai manufacturer quote, Saudi Arabia supplier quote"
        canonical={`${BASE_URL}/rfq`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Request for Quote (RFQ) – SuperMiddleEast",
            "description": "Submit your buying requirements and receive competitive quotes from verified Middle East factories.",
            "url": `${BASE_URL}/rfq`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "RFQ", "item": `${BASE_URL}/rfq` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How does the RFQ process work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Submit your buying requirements, verified factories respond with competitive quotes, you compare offers and pick the best deal, then place your order."
                }
              },
              {
                "@type": "Question",
                "name": "How long does it take to receive quotes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You will start receiving quotes within 24–48 hours of submitting your RFQ."
                }
              },
              {
                "@type": "Question",
                "name": "How many quotes will I receive?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "On average, buyers receive 5–8 quotes per RFQ from verified factories."
                }
              }
            ]
          }
        ]}
      />
      <Navbar />
      <div className="pt-24 pb-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <i className="ri-file-list-3-line"></i> Request for Quote
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tell Factories What You Need
            </h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Submit your buying requirements and let verified Middle East factories compete to give you the best price.
            </p>
          </div>

          {/* How it works */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: "ri-file-list-3-line", step: "1", title: "Submit RFQ", desc: "Describe what you need, quantity, and budget" },
              { icon: "ri-building-2-line", step: "2", title: "Factories Respond", desc: "Verified factories send you competitive quotes" },
              { icon: "ri-scales-3-line", step: "3", title: "Compare Quotes", desc: "Review offers and pick the best deal" },
              { icon: "ri-check-double-line", step: "4", title: "Place Order", desc: "Confirm your order and start production" },
            ].map((s) => (
              <div key={s.step} className="bg-amber-50/60 border border-amber-100 rounded-2xl p-4 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500 text-white rounded-full mx-auto mb-3 font-black text-sm">{s.step}</div>
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-xl mx-auto mb-2">
                  <i className={`${s.icon} text-amber-600 text-base`}></i>
                </div>
                <p className="font-bold text-gray-900 text-sm mb-1">{s.title}</p>
                <p className="text-gray-400 text-xs">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-amber-50 border border-amber-100 rounded-2xl p-1.5 mb-8 w-fit">
            <button
              onClick={() => setActiveTab("submit")}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === "submit" ? "bg-amber-500 text-white" : "text-gray-500 hover:text-amber-600"}`}
            >
              <i className="ri-add-circle-line"></i> Submit New RFQ
            </button>
            <button
              onClick={() => setActiveTab("browse")}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === "browse" ? "bg-amber-500 text-white" : "text-gray-500 hover:text-amber-600"}`}
            >
              <i className="ri-list-check-2"></i> Browse Open RFQs
            </button>
          </div>

          {/* Submit Form */}
          {activeTab === "submit" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {submitted ? (
                  <div className="bg-white border border-amber-100 rounded-3xl p-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                      <i className="ri-checkbox-circle-fill text-3xl text-amber-500"></i>
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                      RFQ Submitted!
                    </h2>
                    <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                      Your request has been sent to verified factories. You&apos;ll start receiving quotes within 24–48 hours.
                    </p>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
                      <p className="text-xs font-bold text-amber-700 mb-2">What happens next?</p>
                      <ul className="space-y-1.5 text-xs text-gray-600">
                        <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Factories review your requirements</li>
                        <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>You receive quotes via email within 24–48h</li>
                        <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Compare and negotiate directly with factories</li>
                        <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-amber-500 mt-0.5"></i>Place your order when ready</li>
                      </ul>
                    </div>
                    <div className="flex gap-3 justify-center">
                      <button onClick={() => setSubmitted(false)} className="border border-amber-200 text-amber-700 font-bold px-5 py-2.5 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
                        Submit Another RFQ
                      </button>
                      <button onClick={() => setActiveTab("browse")} className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                        Browse Open RFQs
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="bg-white border border-amber-100 rounded-3xl p-6 md:p-8 space-y-5">
                    <h2 className="font-black text-gray-900 text-xl">Your Buying Requirements</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Name <span className="text-red-400">*</span></label>
                        <input name="buyerName" required placeholder="Full name" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                        <input name="email" required type="email" placeholder="you@company.com" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Your Country <span className="text-red-400">*</span></label>
                        <select name="buyerCountry" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                          <option value="">Select country</option>
                          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company Name</label>
                        <input name="companyName" placeholder="Your company (optional)" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                      </div>
                    </div>

                    <div className="border-t border-amber-50 pt-4">
                      <h3 className="font-bold text-gray-800 text-sm mb-4">Product Requirements</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Product Name / Description <span className="text-red-400">*</span></label>
                          <input name="productName" required placeholder="e.g. Industrial LED Panels 200W, Arabic Oud Perfume..." className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Product Category <span className="text-red-400">*</span></label>
                          <select name="category" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                            <option value="">Select category</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target Factory Country</label>
                          <select name="targetCountry" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                            <option value="">Any country</option>
                            {TARGET_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Required Quantity <span className="text-red-400">*</span></label>
                          <input name="quantity" required placeholder="e.g. 500 units, 1000 pieces" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Order Type <span className="text-red-400">*</span></label>
                          <select name="orderType" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                            <option value="">Select type</option>
                            <option>Sample Order</option>
                            <option>Bulk Order</option>
                            <option>Both (Sample first, then Bulk)</option>
                            <option>Dropshipping</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Budget Range (USD)</label>
                          <input name="budget" placeholder="e.g. $5,000 – $10,000" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Required Delivery Date</label>
                          <input name="deliveryDate" type="date" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Detailed Requirements</label>
                      <textarea
                        name="details"
                        rows={4}
                        maxLength={500}
                        placeholder="Describe specifications, materials, certifications needed, packaging requirements, custom branding, etc..."
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Certifications Required</label>
                        <input name="certifications" placeholder="e.g. Halal, ISO 9001, CE, FDA..." className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred Payment Terms</label>
                        <select name="paymentTerms" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white">
                          <option value="">Select terms</option>
                          <option>T/T (Bank Transfer)</option>
                          <option>L/C (Letter of Credit)</option>
                          <option>PayPal</option>
                          <option>Western Union</option>
                          <option>Flexible / Negotiable</option>
                        </select>
                      </div>
                    </div>

                    <button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-2xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-base">
                      <i className="ri-send-plane-fill"></i> Submit RFQ to All Verified Factories
                    </button>
                  </form>
                )}
              </div>

              {/* Sidebar tips */}
              <div className="space-y-5">
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                  <h4 className="font-black text-gray-900 text-sm mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-amber-500"></i> Tips for Better Quotes
                  </h4>
                  <ul className="space-y-2 text-xs text-gray-600">
                    {[
                      "Be specific about product specs and materials",
                      "Include your target price range",
                      "Mention any certifications needed (Halal, ISO, etc.)",
                      "State your delivery timeline clearly",
                      "Mention if you need private label / custom branding",
                      "Include photos or reference products if possible",
                    ].map((tip, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white border border-amber-100 rounded-2xl p-5">
                  <h4 className="font-black text-gray-900 text-sm mb-3 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-emerald-500"></i> Platform Stats
                  </h4>
                  <div className="space-y-3">
                    {[
                      { label: "Avg. Response Time", value: "< 24 hours" },
                      { label: "Avg. Quotes per RFQ", value: "5–8 quotes" },
                      { label: "Verified Factories", value: "18+ factories" },
                      { label: "Countries Covered", value: "3 countries" },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex justify-between text-xs">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-bold text-gray-900">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
                  <h4 className="font-black text-base mb-2">Need Urgent Sourcing?</h4>
                  <p className="text-amber-100 text-xs mb-3">Contact our sourcing team directly for priority assistance.</p>
                  <a href="mailto:sourcing@supermiddleeast.com" className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-mail-line"></i> sourcing@supermiddleeast.com
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Browse RFQs */}
          {activeTab === "browse" && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <p className="text-gray-500 text-sm"><strong className="text-gray-900">{recentRFQs.filter((r) => r.status === "open").length}</strong> open RFQs waiting for quotes</p>
                <button onClick={() => setActiveTab("submit")} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i> Submit Your RFQ
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {recentRFQs.map((rfq) => (
                  <div key={rfq.id} className="bg-white border border-amber-100 rounded-2xl p-5 hover:border-amber-300 transition-all">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${rfq.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                        {rfq.status === "open" ? "Open" : "Closed"}
                      </span>
                      <span className="text-xs text-gray-400">{rfq.date}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1 leading-tight">{rfq.product}</h3>
                    <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{rfq.category}</span>
                    <div className="mt-3 space-y-1.5 text-xs text-gray-500">
                      <div className="flex items-center gap-2">
                        <i className="ri-user-line text-amber-500"></i>
                        <span>{rfq.buyer} · {rfq.country}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-stack-line text-amber-500"></i>
                        <span>Qty: <strong className="text-gray-800">{rfq.qty}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-money-dollar-circle-line text-emerald-500"></i>
                        <span>Budget: <strong className="text-gray-800">{rfq.budget}</strong></span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-amber-50">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-message-3-line text-amber-400"></i>
                        {rfq.responses} {rfq.responses === 1 ? "response" : "responses"}
                      </span>
                      {rfq.status === "open" && (
                        <button className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                          Submit Quote
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RFQPage;
